/**
 * LearningPhase Component
 *
 * Two-column layout for Round 2 (Information Phase):
 * - Left column: Learning outcomes, example questions, and what to focus on
 * - Right column: Learning material (AI Chat, PDF, Web Search, or Alternative Topic)
 *
 * For Teachers/Learners (actively learning):
 * - Shows learning objectives
 * - Shows example questions from the quiz
 * - Shows quiz level expectations (Remember, Understand, Apply)
 *
 * For Students (waiting):
 * - Shows alternative topic to read
 * - Shows notification when Teacher is ready
 */

import React from "react";
import { usePlayer, usePlayers, useGame, useRound } from "@empirica/core/player/classic/react";

/**
 * Parse instruction string and render as numbered list
 * Format: "(1) Point one (2) Point two (3) Point three"
 * or "1. Point one 2. Point two 3. Point three"
 *
 * Handles nested parentheses in points like "(a+b = -B/A)"
 */
function LearningOutcomes({ instruction, color = "blue", isSingleMode = false }) {
  if (!instruction) {
    return (
      <p className="text-base text-blue-800">
        {isSingleMode
          ? "Study this topic well enough to answer questions about it."
          : "Study this topic well enough to explain it to your partner."}
      </p>
    );
  }

  const colorClasses = {
    blue: "text-gray-700",
    emerald: "text-emerald-800"
  };

  let points = [];

  // Try to parse "(1) ... (2) ... (3) ..." format
  // Split by the numbered markers, capturing content between them
  const parenPattern = /\((\d+)\)\s*/g;
  const parenMatches = [...instruction.matchAll(parenPattern)];

  if (parenMatches.length > 0) {
    // Extract text between each numbered marker
    for (let i = 0; i < parenMatches.length; i++) {
      const startIdx = parenMatches[i].index + parenMatches[i][0].length;
      const endIdx = i + 1 < parenMatches.length ? parenMatches[i + 1].index : instruction.length;
      const content = instruction.slice(startIdx, endIdx).trim();
      if (content) {
        points.push(content);
      }
    }
  }

  // Fallback: try "1. ... 2. ... 3. ..." format
  if (points.length === 0) {
    const dotPattern = /(\d+)\.\s*/g;
    const dotMatches = [...instruction.matchAll(dotPattern)];

    if (dotMatches.length > 0) {
      for (let i = 0; i < dotMatches.length; i++) {
        const startIdx = dotMatches[i].index + dotMatches[i][0].length;
        const endIdx = i + 1 < dotMatches.length ? dotMatches[i + 1].index : instruction.length;
        const content = instruction.slice(startIdx, endIdx).trim();
        if (content) {
          points.push(content);
        }
      }
    }
  }

  if (points.length > 0) {
    return (
      <ul className={`text-base space-y-3 ${colorClasses[color]}`}>
        {points.map((point, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
              {idx + 1}
            </span>
            <span className="pt-0.5 leading-relaxed">{point}</span>
          </li>
        ))}
      </ul>
    );
  }

  // Fallback: just show the instruction as text
  return (
    <p className={`text-base leading-relaxed ${colorClasses[color]}`}>
      {instruction}
    </p>
  );
}

export function LearningPhase({ children }) {
  const player = usePlayer();
  const players = usePlayers();
  const game = useGame();
  const round = useRound();

  const marker = player.get("marker");
  const secondaryMarker = player.get("secondary_marker");
  const config = game?.get("config");
  const pairedConfig = game?.get("pairedConfig");

  // Check game mode for proper role detection
  const gameMode = game?.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";

  // In SINGLE mode, marker=0 means Learner, not Teacher
  // isTeacher is true for DYAD Teachers who actively learn content
  // In SINGLE mode, Learner also actively learns content (marker=0)
  const isTeacher = marker === 0;
  const isLearner = isSingleMode && marker === 0;

  // Check if teacher (marker=0) has submitted - for student notification
  const teacher = players.find(p => p.get("marker") === 0);
  const teacherIsReady = teacher?.stage?.get("submit") === true;

  // Teacher sees main topic, Student sees alternative (paired) topic
  const topicName = isTeacher
    ? (config?.name || "the topic")
    : (pairedConfig?.name || round?.get("pairedTopicName") || "Alternative Topic");

  // Determine learning method label
  const getLearningMethod = () => {
    if (!isTeacher) return "Reading";
    if (secondaryMarker === "ai") return "AI Chat";
    if (secondaryMarker === "ai-fixed") return "AI Tutor";
    if (secondaryMarker === "ai-pdf") return "AI PDF";
    if (secondaryMarker === "web") return "Web Search";
    return "PDF";
  };

  const methodLabel = getLearningMethod();

  return (
    <div className="h-full w-full flex">
      {/* Left Column: Learning Guide - 1/3 of screen */}
      <div className="w-1/3 min-w-[380px] max-w-[480px] flex-shrink-0 bg-gray-50 border-r border-gray-200 p-5 overflow-y-auto">

        {/* Topic & Method Header */}
        <div className={`rounded-lg p-4 mb-4 ${isLearner ? 'bg-violet-600' : isTeacher ? 'bg-blue-600' : 'bg-emerald-600'} text-white shadow-md`}>
          <span className="text-sm font-medium uppercase tracking-wide opacity-80">{methodLabel}</span>
          <h2 className="text-lg font-bold leading-tight mt-1">{topicName}</h2>
        </div>

        {/* Focus Area - Key instruction */}
        {isTeacher ? (
          <div className="rounded-lg p-4 mb-4 border-2 border-red-400 bg-red-50 select-none shadow-sm">
            <div className="mb-3">
              <h3 className="text-sm font-bold text-red-800 uppercase tracking-wide">
                Learning Objectives
              </h3>
            </div>
            <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-3">
              <p className="text-sm text-red-800 font-semibold leading-relaxed">
                {isSingleMode
                  ? "Understand these points well enough to answer questions about them:"
                  : "Study these points so you can clearly explain each one to your partner:"}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-red-200">
              <LearningOutcomes instruction={config?.instructionExplainer} color="blue" isSingleMode={isSingleMode} />
            </div>
            {!isSingleMode && (
              <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 leading-relaxed">
                  Your goal is to be able to explain this topic yourself. Your partner will not see this material, so they can only learn what you explain to them.
                </p>
              </div>
            )}
            <div className="mt-3 p-3 bg-amber-100 border border-amber-300 rounded-lg">
              <p className="text-sm text-amber-800 font-medium">
                {isSingleMode
                  ? "You will be quizzed on these topics."
                  : "You will explain these to your partner, and both of you will be quizzed on them."}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mb-4">
            {/* Teacher Ready Notification */}
            {teacherIsReady && (
              <div className="rounded-xl p-4 border-2 border-green-400 bg-green-100 animate-pulse">
                <div className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-base font-bold text-green-800">Your partner is ready!</span>
                </div>
                <p className="text-sm text-green-700 mt-2">
                  Click "I'm ready, continue" when you've finished reading.
                </p>
              </div>
            )}

            <div className="rounded-xl p-4 border border-emerald-200 bg-emerald-50">
              <h3 className="text-base font-semibold mb-2 text-emerald-800">
                While You Wait
              </h3>
              <div className="text-sm leading-relaxed text-emerald-800 space-y-2">
                <p>
                  Read this article while your partner prepares to explain the main topic to you.
                </p>
                <p>
                  <strong>Why a different topic?</strong> This keeps both participants actively engaged and helps us compare different learning approaches.
                </p>
                {!teacherIsReady && (
                  <p className="text-emerald-600 italic">
                    Your partner will teach you the main topic via chat shortly.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Learning Tips - condition-specific */}
        {isTeacher && (
          <div className="mt-4">
            {(secondaryMarker === "ai" || secondaryMarker === "ai-fixed") && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Ask questions.</strong> The AI can explain concepts, give examples, and clarify anything you don't understand{isSingleMode ? "." : ", so you can explain it clearly yourself."}
                </p>
              </div>
            )}
            {secondaryMarker === "web" && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Search strategically.</strong> Find reliable sources that explain the key concepts clearly{isSingleMode ? "." : ", so you can explain them yourself."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right Column: Material - full height */}
      <div className="flex-1 min-w-0 h-full overflow-hidden">
        {children}
      </div>
    </div>
  );
}
